import translation from 'locales/translation';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { PautaEvento } from 'api/Assessor/pautas';

import { CircleTimes, PersonGraph } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import { ComputeVotes } from 'components/Modal/ComputeVotes';
import InconsistenciaVotosModal, {
  IInconsistenciaVotosModalGenericProps,
} from 'components/Modal/__LEGACY__/InconsistenciaVotosModal/InconsistenciaVotosModal';
import {
  AssembleiaPautaStatus,
  getNumberByAssembleiaPautaStatus,
} from 'helpers/enums/AssembleiaPautaStatus';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import {
  AssembleiasIcon,
  BarChartIcon,
  Button,
  ButtonThemes,
  ConfirmModal,
  ModalSizes,
  ScreenSize,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { useDispatch } from 'react-redux';
import {
  assembleiasAssessorActionsAlterarSituacaoPautaPresencial,
  assembleiasAssessorActionsLiberarResultadoPresencialPauta,
} from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorEvent } from 'store/modules/AssembleiasAssessorReducer';
import { IAuthUserReducer } from 'store/modules/AuthReducer';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import useStyle from '../style';

interface Props {
  pauta: PautaEvento;
  evento: IAssembleiasAssessorEvent;
  usuario: IAuthUserReducer;
}

export default function ComputacaoVotoAction({
  pauta,
  evento,
  usuario,
}: Props) {
  const classes = useStyle();
  const pautaStatus = useMemo(() => {
    return getNumberByAssembleiaPautaStatus(pauta.statusPresencial);
  }, [pauta]);
  const [disabledButton, setDisabledButton] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { showToast } = useContext(LayoutContext);

  const hasPermissionToFinish = useMemo(() => {
    if (
      usuario?.perfil.id === 'pertencer_consulta' &&
      !usuario?.perfil.assessorRoute.assembleias.pautas.alterarSituacao &&
      evento?.tipo === 'REU'
    ) {
      return true;
    } else {
      return (
        usuario?.perfil.assessorRoute.assembleias.pautas.alterarSituacao ??
        false
      );
    }
  }, [
    evento?.tipo,
    usuario?.perfil.assessorRoute.assembleias.pautas.alterarSituacao,
    usuario?.perfil.id,
  ]);

  const openVotosInconsistentesModal = useCallback(
    (err: any) => {
      const modalKey: string = getUniqueKey();
      dispatch(
        modalsActionsOpen(
          InconsistenciaVotosModal,
          {
            genericProps: {
              key: modalKey,
              idEvento: evento?.id,
              present: err,
              title: pauta.titulo,
            } as IInconsistenciaVotosModalGenericProps,
          },
          modalKey
        )
      );
    },
    [dispatch, evento?.id, pauta.titulo]
  );

  const encerrarVotacao = useCallback(() => {
    dispatch(
      assembleiasAssessorActionsAlterarSituacaoPautaPresencial(
        evento.id,
        pauta.idPauta,
        0,
        AssembleiaPautaStatus.Encerrada,
        () => {},
        (error: any) => {
          openVotosInconsistentesModal(error?.response?.data);
          if (!error?.response?.data.inconsistencies) {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: translation.ComputeVotes.errLiberarPautaPresencial,
            });
          }
        }
      )
    );
  }, [
    dispatch,
    evento.id,
    pauta.idPauta,
    openVotosInconsistentesModal,
    showToast,
  ]);

  const abrirVotacao = useCallback(() => {
    dispatch(
      assembleiasAssessorActionsAlterarSituacaoPautaPresencial(
        evento.id,
        pauta.idPauta,
        0,
        AssembleiaPautaStatus.Liberada,
        () => {},
        (err) => {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label:
              err!?.response!?.data.message === 'pauta.resultadoJaLiberado'
                ? 'Resultado da pauta está liberado.'
                : translation.ComputeVotes.errReabrirPautaPresencial,
          });
        }
      )
    );
  }, [dispatch, evento.id, pauta.idPauta, showToast]);

  const openComputeVotes = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(ComputeVotes, {
        idPauta: pauta.idPauta,
        modalKey: modalKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        titlePauta: pauta.titulo,
        title: translation.ComputeVotes.votacaoPresencialTitle,
        eventType: evento.tipo,
        isReadOnly: false,
        onClose: () => {
          dispatch(closeModal(modalKey));
        },
        onConfirm: () => {
          return new Promise<void>((resolve: any) => {
            resolve();
            dispatch(closeModal(modalKey));
          });
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pauta]);

  const liberarResultadoPresencial = useCallback(() => {
    const mk = getUniqueKey();
    dispatch(
      openModal(ConfirmModal, {
        modalKey: mk,
        size: ModalSizes.Large,
        title: translation.ComputeVotes.liberarResultadosTitle,
        mobileOn: ScreenSize.xs,
        children: translation.ComputeVotes.atencaoLiberarResultadosSubtitle,

        onClose: () => {
          dispatch(closeModal(mk));
        },
        onCancel: () => {
          dispatch(closeModal(mk));
        },
        onConfirm: () => {
          return new Promise<void>((resolve: (evt?: any) => void) => {
            dispatch(
              assembleiasAssessorActionsLiberarResultadoPresencialPauta(
                evento.id,
                pauta!?.idPauta,
                () => {
                  dispatch(closeModal(mk));
                  setDisabledButton(true);
                },
                () => {
                  showToast({
                    pauseOnFocusLoss: true,
                    theme: ToastTypes.Error,
                    showStatusBar: true,
                    timeout: 6000,
                    label:
                      translation.ComputeVotes.errLiberarResultadoPresencial,
                  });
                }
              )
            );
            resolve();
          });
        },
      })
    );
  }, [dispatch, evento.id, pauta, showToast]);

  useEffect(() => {
    if (pauta.resultadoLiberadoPresencial) {
      setDisabledButton(true);
    }
  }, [pauta]);

  return (
    <Fragment>
      {pautaStatus === AssembleiaPautaStatus.Liberada ? (
        <>
          <Button
            className={classes.botaoAcoes}
            theme={ButtonThemes.Secondary}
            onClick={encerrarVotacao}
            leftIcon={<CircleTimes />}
            disabled={!hasPermissionToFinish}
          >
            {translation.AssembleiaPauta.acoes.encerrarVotacao}
          </Button>
        </>
      ) : undefined}
      {pautaStatus === AssembleiaPautaStatus.Liberada ||
      pautaStatus === AssembleiaPautaStatus.Bloqueada ? (
        <Button
          className={classes.botaoAcoes}
          theme={ButtonThemes.Primary}
          onClick={openComputeVotes}
          leftIcon={<PersonGraph />}
          disabled={
            pautaStatus === AssembleiaPautaStatus.Bloqueada ||
            !pauta.itens.length
          }
        >
          {translation.AssembleiaPauta.acoes.computarVotos}
        </Button>
      ) : undefined}
      {pautaStatus === AssembleiaPautaStatus.Encerrada ? (
        <Fragment>
          <Button
            className={classes.botaoAcoes}
            theme={ButtonThemes.Secondary}
            onClick={liberarResultadoPresencial}
            leftIcon={<BarChartIcon />}
            disabled={disabledButton}
          >
            {translation.AssembleiaPauta.acoes.liberarResultados}
          </Button>
          <Button
            className={classes.botaoAcoes}
            theme={ButtonThemes.Primary}
            onClick={abrirVotacao}
            leftIcon={<AssembleiasIcon />}
            disabled={disabledButton}
          >
            {translation.AssembleiaPauta.acoes.reabrirVoto}
          </Button>
        </Fragment>
      ) : undefined}
    </Fragment>
  );
}
